import AlertCircle from "@bevco/MobiusOverrides/Icons/AlertCircle";
import CheckCircle from "@bevco/MobiusOverrides/Icons/CheckCircle";
import Quote from "@bevco/MobiusOverrides/Icons/Quote";
import { isQuoteRequired } from "@bevco/Store/Handlers/Utilities/useCustomQuoteRequired";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import isTouchDevice from "@insite/client-framework/Common/Utilities/isTouchDevice";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { AttributeTypeModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import Calendar from "@insite/mobius/Icons/Calendar";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { resolveColor } from "@insite/mobius/utilities";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import useEmblaCarousel from "embla-carousel-react";
import React, { createRef, ReactNode, useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    id: string;
    extendedStyles?: CarouselStyles;
    title: string;
    hideTitle?: boolean;
    maxNumberOfColumns: number;
    slideDetails: ProductCarouselSlideDetails;
}

const mapStateToProps = (state: ApplicationState) => {
    const productInfosByProductId = state.components.productInfoLists.productInfoListById["product-compare-carousel-id"]?.productInfoByProductId;
    return {
        productInfosByProductId,
    }
};

type Props = OwnProps & ThemeProps<BaseTheme> & HasShellContext & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselSlideDetails {
    slides: CarouselSlide[];
    products: ProductModel[];
    attributeTypes: AttributeTypeModel[];
}

export interface CarouselSlide {
    id: string;
    renderComponent: (options: CarouselSlideOptions) => ReactNode;
}

export interface CarouselSlideOptions {}

export interface CarouselStyles {
    titleText?: TypographyPresentationProps;
    skipCarouselButton?: SkipNavStyles;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselContentEmptyCellGridItem?: GridItemProps;
    carouselCellGridItem?: GridItemProps;
    carouselContentContainer?: GridContainerProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    attributeLabelsCellGridItem?: GridItemProps;
    attributeLabelsGridContainer?: GridContainerProps;
    attributeLabelGridItem?: GridItemProps;
    attributeLabelText?: TypographyPresentationProps;
    attributeValuesCellGridItem?: GridItemProps;
    attributeValuesWrapper?: InjectableCss;
    attributeValuesContainer?: InjectableCss;
    attributeValuesSlidesContainer?: InjectableCss;
    attributeValueSlide?: InjectableCss;
    attributeValueSlideInner?: InjectableCss;
    attributeValuesGridContainer?: GridContainerProps;
    attributeValueGridItem?: GridItemProps;
    attributeValueText?: TypographyPresentationProps;
    // VERNDALE CUSTOMIZATION START
    tooltipStyles?: TooltipPresentationProps;
    tooltipTrigger?: InjectableCss;
    triggerText?: TypographyProps;
    availabilityTooltipWrapper?: InjectableCss;
    availabilityTooltipTitle?: TypographyProps;
    availabilityTooltipText?: TypographyProps;
}

export const carouselStyles: CarouselStyles = {
    titleText: {
        variant: "h2",
        css: css`
            text-align: center;
            margin-bottom: 10px;
        `,
    },
    mainContainer: {
        gap: 0,
        css: css`
            margin: 0 auto;
        `,
    },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-start;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                ])}
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
        `,
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                ])}
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                ])}
        `,
    },
    carouselContentContainer: {
        gap: 0,
    },
    carouselContentEmptyCellGridItem: {
        width: 4,
    },
    carouselCellGridItem: {
        width: 8,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
        `,
    },
    carouselSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: 0 20px;
            display: flex;
            flex-direction: column;
        `,
    },
    attributeLabelsCellGridItem: {
        width: 4,
        css: css`
            border: ${({ theme }) => resolveColor("common.border", theme)} solid 1px;
            border-right: 0;
            border-bottom: 0;
        `,
    },
    attributeLabelsGridContainer: {
        gap: 0,
        css: css`
            z-index: 0;
        `,
    },
    attributeLabelGridItem: {
        width: 12,
        css: css`
            border-bottom: ${({ theme }) => resolveColor("common.border", theme)} solid 1px;
            padding: 0.8rem 0.8rem;
            &:nth-child(even) {
                background-color: ${getColor("common.global.white")};
            }
        `,
    },
    attributeLabelText: {
        ellipsis: true,
        weight: "bold",
    },
    attributeValuesCellGridItem: {
        width: 8,
        css: css`
            border: ${({ theme }) => resolveColor("common.border", theme)} solid 1px;
            border-left: 0;
            border-bottom: 0;
        `,
    },
    attributeValuesWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    attributeValuesContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    attributeValuesSlidesContainer: {
        css: css`
            display: flex;
        `,
    },
    attributeValueSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;
        `,
    },
    attributeValueSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: 0;
            display: flex;
            flex-direction: column;
        `,
    },
    attributeValuesGridContainer: {
        gap: 0,
    },
    attributeValueGridItem: {
        width: 12,
        css: css`
            border-bottom-style: solid;
            border-bottom-width: 1px;
            border-bottom-color: ${({ theme }) => resolveColor("common.border", theme)};
            padding: 0.8rem 0.8rem;
            &:nth-child(even) {
                background-color: ${getColor("common.global.white")};
            }
        `,
    },
    attributeValueText: {
        ellipsis: true,
        css: css`
            &.overflow {
                overflow: visible !important;
                height: 24px;

                button {
                    max-height: 24px;
                }
            }
        `,
    },

    // VERNDALE CUSTOMIZATION START
    tooltipTrigger: {
        css: css`
            display: flex;
            align-items: center;
            border-radius: 5px;
            margin-right: 20px;

            ${IconWrapper} {
                margin-right: 10px;
            }
        `,
    },

    triggerText: {
        css: css`
            text-decoration: underline;
            text-decoration-style: dashed;
            text-underline-offset: 5px;
        `,
    },

    availabilityTooltipWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            width: 100%;
            text-align: left;
            white-space: normal;
        `,
    },

    availabilityTooltipTitle: {
        css: css`
            text-align: left;
            font-weight: bold;
            margin-bottom: 0;
            width: 100%;
            white-space: normal;
        `,
    },

    availabilityTooltipText: {
        css: css`
            text-align: left;
            width: 100%;
            white-space: normal;
        `,
    },
    tooltipStyles:{
        cssOverrides: {
            tooltipContainer: css`
                width: 140px !important;
            `,
        },
    },
};

const Carousel = ({
    shellContext,
    theme,
    id,
    extendedStyles,
    title,
    hideTitle,
    maxNumberOfColumns,
    slideDetails,
    productInfosByProductId,
}: Props) => {
    const afterCarousel = createRef<HTMLSpanElement>();
    const [styles] = useState(() => mergeToNew(carouselStyles, extendedStyles));

    const [emblaRef, embla] = useEmblaCarousel();
    const [emblaSecondRef, emblaSecond] = useEmblaCarousel();
    const [windowResizeTime, setWindowResizeTime] = useState(0);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const setCanScroll = () => {
        if (shellContext.isInShell) {
            return;
        }
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    const getSlidesToScroll = useCallback(() => {
        if (typeof window === "undefined") {
            return maxNumberOfColumns;
        }

        let localSlidesToScroll: number;
        const { innerWidth } = window;
        if (innerWidth < theme.breakpoints.values[1]) {
            localSlidesToScroll = 1;
        } else if (innerWidth < theme.breakpoints.values[2]) {
            localSlidesToScroll = 1;
        } else if (innerWidth < theme.breakpoints.values[3]) {
            localSlidesToScroll = 2;
        } else {
            localSlidesToScroll = 3;
        }
        return Math.min(localSlidesToScroll, maxNumberOfColumns);
    }, [maxNumberOfColumns]);

    const [slidesToScroll, setSlidesToScroll] = useState(0);

    useEffect(() => {
        const newSlidesToScroll = getSlidesToScroll();
        setSlidesToScroll(newSlidesToScroll);
    }, [maxNumberOfColumns, windowResizeTime]);

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);

        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, [embla]);

    useEffect(() => {
        if (!emblaSecond) {
            return;
        }

        emblaSecond.on("init", setCanScroll);
        emblaSecond.on("select", setCanScroll);
    }, [emblaSecond]);

    useEffect(() => {
        if (!embla || !emblaSecond || !slideDetails || slideDetails.slides.length === 0) {
            return;
        }

        const draggable = isTouchDevice();
        embla.reInit({
            align: "start",
            slidesToScroll,
            watchDrag: draggable,
            loop: true,
        });
        emblaSecond.reInit({
            align: "start",
            slidesToScroll,
            watchDrag: draggable,
            loop: true,
        });
        setCanScroll();

        const onMainPointerUp = () => {
            emblaSecond.scrollTo(embla.selectedScrollSnap(), true);
        };
        const onSecondPointerUp = () => {
            embla.scrollTo(emblaSecond.selectedScrollSnap(), true);
        };

        embla.on("pointerUp", onMainPointerUp);
        emblaSecond.on("pointerUp", onSecondPointerUp);

        return () => {
            embla.off("pointerUp", onMainPointerUp);
            emblaSecond.off("pointerUp", onSecondPointerUp);
        };
    }, [embla, emblaSecond, slidesToScroll, slideDetails]);

    const getProductAttributeValue = (product: ProductModel, attribute: AttributeTypeModel) => {
        const productAttributeValue = product.attributeTypes
            ?.filter(a => a.id === attribute.id)
            .map(a => a.attributeValues || [])
            .reduce((acc, currentValue) => [...acc, ...currentValue], []);

        if (productAttributeValue && productAttributeValue.length > 0) {
            return (
                <Typography
                    aria-describedby={`carousel-${id}_attribute-label-${attribute.id}`}
                    {...styles.attributeValueText}
                >
                    {productAttributeValue.map(o => o.valueDisplay).join(", ")}
                </Typography>
            );
        }

        return <>&nbsp;</>;
    };

    if (!slideDetails || slideDetails.slides.length === 0) {
        return null;
    }

    // VERNDALE CUSTOMIZATION START - Adding custom tooltip
    const quoteColor: string = theme.colors.secondary.main;
    const inStockColor: string = theme.colors.success.main;
    const outOfStockColor: string = theme.colors.danger.main;

    const textContentQuote = (
        <StyledWrapper color={quoteColor} {...styles.tooltipTrigger}>
            <Icon color={quoteColor} src={Quote} />
            <Typography color={quoteColor} {...styles.triggerText} data-test-selector="availabilityMessage">
                {translate("Quote Required")}
            </Typography>
        </StyledWrapper>
    );

    const textContentQuoteAndBackOrder = (
        <StyledWrapper color={quoteColor} {...styles.tooltipTrigger}>
            <Icon color={quoteColor} src={Calendar} />
            <Typography color={quoteColor} {...styles.triggerText} data-test-selector="availabilityMessage">
                {translate("Quote Required")}
            </Typography>
        </StyledWrapper>
    );

    const textContentInStock = (
        <StyledWrapper color={inStockColor} {...styles.tooltipTrigger}>
            <Icon color={inStockColor} src={CheckCircle} />
            <Typography color={inStockColor} {...styles.triggerText} data-test-selector="availabilityMessage">
                {translate("In Stock")}
            </Typography>
        </StyledWrapper>
    );
    const textContentInStockAndBackOrder = (leadTimeValue: string, hasLeadTime: boolean) => {
        return (
            <StyledWrapper color={inStockColor} {...styles.tooltipTrigger}>
                <Icon color={inStockColor} src={Calendar} />
                <Typography color={inStockColor} {...styles.triggerText} data-test-selector="availabilityMessage">
                    {translate("Typically ships in {0} week(s)", `${hasLeadTime ? leadTimeValue : "0"}`)}
                </Typography>
            </StyledWrapper>
        );
    };

    const textContentOutOfStock = (
        <StyledWrapper color={outOfStockColor} {...styles.tooltipTrigger}>
            <Icon color={outOfStockColor} src={AlertCircle} />
            <Typography color={outOfStockColor} {...styles.triggerText} data-test-selector="availabilityMessage">
                {translate("Out of Stock")}
            </Typography>
        </StyledWrapper>
    );

    const tooltip = (type: string, text: any, title: any, leadTimeValue?: string, hasLeadTime?: boolean) => {
        return (
            <Tooltip
                {...styles.tooltipStyles}
                triggerComponent={
                    type === "quote"
                        ? textContentQuote
                        : type === "quoteAndBackOrder"
                          ? textContentQuoteAndBackOrder
                          : type === "inStockAndBackOrder"
                          ? textContentInStockAndBackOrder(leadTimeValue ?? "", hasLeadTime ?? false)
                          : type === "ouOfStock"
                          ? textContentOutOfStock
                          : textContentInStock
                }
                text={
                    (
                        <StyledWrapper {...styles.availabilityTooltipWrapper}>
                            <Typography {...styles.availabilityTooltipTitle}>{title}</Typography>
                            <Typography {...styles.availabilityTooltipText}>{text}</Typography>
                        </StyledWrapper>
                    ) as any
                }
            ></Tooltip>
        );
    };
    // VERNDALE CUSTOMIZATION END - Adding custom tooltip

    // VERNDALE CUSTOMIZATION START - Check if some product has ecommMOQ and incrementalEcommMOQ or isCustNCNR
    const hasEcommMOQ = slideDetails.products.some(product => product.properties?.ecommMOQ !== undefined);

    const hasIncrementalEcommMOQ = slideDetails.products.some(
        product => product.properties?.incrementalEcommMOQ !== undefined,
    );

    const hasReturnableValue = slideDetails.products.some(
        product =>
            (product.properties?.isCustNCNR === "true" || product.properties?.isCustNCNR === "false") &&
            !product.quoteRequired,
    );

    return (
        <>
            <SkipNav
                text={translate("Skip Carousel")}
                extendedStyles={styles.skipCarouselButton}
                destination={afterCarousel}
            />
            {hideTitle ? (
                <VisuallyHidden>
                    <Typography {...styles.titleText}>{title}</Typography>
                </VisuallyHidden>
            ) : (
                <Typography {...styles.titleText}>{title}</Typography>
            )}

            <GridContainer {...styles.mainContainer} data-test-selector="productCompareCarousel">
                <GridItem {...styles.prevArrowGridItem}>
                    {slideDetails.slides.length > slidesToScroll && (
                        <Button
                            {...styles.prevArrowButton}
                            onClick={() => {
                                embla && embla.scrollPrev();
                                emblaSecond && emblaSecond.scrollPrev();
                            }}
                            disabled={!canScrollPrev}
                            aria-label={translate("Previous")}
                            data-test-selector="prevBtn"
                        >
                            <ButtonIcon src={ChevronLeft} />
                        </Button>
                    )}
                </GridItem>
                <GridItem {...styles.carouselGridItem}>
                    <GridContainer {...styles.carouselContentContainer}>
                        <GridItem {...styles.carouselContentEmptyCellGridItem}></GridItem>
                        <GridItem {...styles.carouselCellGridItem}>
                            <StyledWrapper {...styles.carouselWrapper}>
                                <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                                    <StyledWrapper {...styles.carouselSlidesContainer} data-test-selector="slides">
                                        {slideDetails.slides.map(slide => (
                                            <StyledWrapper
                                                {...styles.carouselSlide}
                                                key={slide.id}
                                                style={{ width: `calc(100% / ${slidesToScroll})` }}
                                            >
                                                <StyledWrapper
                                                    {...styles.carouselSlideInner}
                                                    id={`slide_${slide.id}`}
                                                    data-test-key={slide.id}
                                                    data-test-selector="carousel_slide"
                                                >
                                                    {slide.renderComponent({})}
                                                </StyledWrapper>
                                            </StyledWrapper>
                                        ))}
                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledWrapper>
                        </GridItem>
                        <GridItem {...styles.attributeLabelsCellGridItem}>
                            <GridContainer {...styles.attributeLabelsGridContainer}>
                                {slideDetails.attributeTypes.map(attribute => (
                                    <GridItem key={attribute.id} {...styles.attributeLabelGridItem}>
                                        <Typography
                                            id={`carousel-${id}_attribute-label-${attribute.id}`}
                                            {...styles.attributeLabelText}
                                        >
                                            {attribute.label || attribute.name}
                                        </Typography>
                                    </GridItem>
                                ))}
                                {/* VERNDALE CUSTOMIZATION START - Additional attributes */}
                                {hasEcommMOQ && (
                                    <GridItem {...styles.attributeLabelGridItem}>
                                        <Typography {...styles.attributeLabelText}>
                                            {translate("Minimum Order")}
                                        </Typography>
                                    </GridItem>
                                )}
                                {hasIncrementalEcommMOQ && (
                                    <GridItem {...styles.attributeLabelGridItem}>
                                        <Typography {...styles.attributeLabelText}>
                                            {translate("Purchase in Multiples of")}
                                        </Typography>
                                    </GridItem>
                                )}
                                <GridItem {...styles.attributeLabelGridItem}>
                                    <Typography {...styles.attributeLabelText}>{translate("Status")}</Typography>
                                </GridItem>
                                {hasReturnableValue && (
                                    <GridItem {...styles.attributeLabelGridItem}>
                                        <Typography {...styles.attributeLabelText}>
                                            {translate("Returnable")}
                                        </Typography>
                                    </GridItem>
                                )}
                                {/* VERNDALE CUSTOMIZATION END - Additional attributes */}
                            </GridContainer>
                        </GridItem>
                        <GridItem {...styles.attributeValuesCellGridItem}>
                            <StyledWrapper {...styles.attributeValuesWrapper}>
                                <StyledWrapper {...styles.attributeValuesContainer} ref={emblaSecondRef}>
                                    <StyledWrapper
                                        {...styles.attributeValuesSlidesContainer}
                                        data-test-selector="attribute-value-slides"
                                    >
                                        {slideDetails.products.map(product => {
                                            const productInfo = productInfosByProductId?.[product.id];
                                            let quoteRequired: boolean | null = null;
                                            if (productInfo && productInfo.pricing && productInfo.inventory) {
                                                quoteRequired = isQuoteRequired({ product, productInfo });
                                            } else {
                                                quoteRequired = product.quoteRequired;
                                            }
                                            const minimumOrderQty =
                                                product.minimumOrderQty ??
                                                +(
                                                    product.properties["ecommMOQ"] ??
                                                    product.properties["minimumOrderQty"] ??
                                                    "0"
                                                );
                                            const multiplesOfQty =
                                                product.detail?.multipleSaleQty ??
                                                +(
                                                    product.properties["incrementalEcommMOQ"] ??
                                                    product.properties["multipleSaleQty"] ??
                                                    "0"
                                                );
                                            const uom = product.properties["baseUnitOfMeasure"] ?? "";

                                            const leadTimeValue = product.properties["deliveryLeadTimeWeeks"];
                                            const hasLeadTime = `${leadTimeValue ?? ""}`.trim() !== "";

                                            return (
                                                <StyledWrapper
                                                    {...styles.attributeValueSlide}
                                                    key={`${product.id}-attributes`}
                                                    style={{ width: `calc(100% / ${slidesToScroll})` }}
                                                >
                                                    <StyledWrapper
                                                        {...styles.attributeValueSlideInner}
                                                        id={`product-attribute_${product.id}`}
                                                        data-test-selector="slideAttributesContainer"
                                                        data-test-key={product.id}
                                                    >
                                                        <GridContainer {...styles.attributeValuesGridContainer}>
                                                            {slideDetails.attributeTypes.map(attribute => (
                                                                <GridItem
                                                                    key={`${product.id}-attribute-${attribute.id}`}
                                                                    {...styles.attributeValueGridItem}
                                                                >
                                                                    {getProductAttributeValue(product, attribute)}
                                                                </GridItem>
                                                            ))}
                                                            {/* VERNDALE CUSTOMIZATION START - Additional attributes */}
                                                            {hasEcommMOQ && (
                                                                <GridItem {...styles.attributeValueGridItem}>
                                                                    <Typography {...styles.attributeValueText}>
                                                                        {minimumOrderQty && uom
                                                                            ? `${minimumOrderQty} ${uom}`
                                                                            : "N/A"}
                                                                    </Typography>
                                                                </GridItem>
                                                            )}
                                                            {hasIncrementalEcommMOQ && (
                                                                <GridItem {...styles.attributeValueGridItem}>
                                                                    <Typography {...styles.attributeValueText}>
                                                                        {multiplesOfQty && uom
                                                                            ? `${multiplesOfQty} ${uom}`
                                                                            : "N/A"}
                                                                    </Typography>
                                                                </GridItem>
                                                            )}
                                                            <GridItem {...styles.attributeValueGridItem}>
                                                                <Typography
                                                                    className="overflow"
                                                                    {...styles.attributeValueText}
                                                                >
                                                                    {product.warehouses?.length === 0 &&
                                                                        !quoteRequired &&
                                                                        tooltip(
                                                                            "ouOfStock",
                                                                            siteMessage(
                                                                                "ProductAvailability_Available_Content_Tooltip",
                                                                            ),
                                                                            siteMessage(
                                                                                "ProductAvailability_Available_Title_Tooltip",
                                                                            ),
                                                                        )}
                                                                    {quoteRequired &&
                                                                        tooltip(
                                                                            "quote",
                                                                            siteMessage(
                                                                                "ProductAvailability_Quote_Content_Tooltip",
                                                                            ),
                                                                            siteMessage(
                                                                                "ProductAvailability_Quote_Title_Tooltip",
                                                                            ),
                                                                        )}
                                                                    {!quoteRequired &&
                                                                        ((product.properties["isBackOrder"] !==
                                                                                undefined &&
                                                                            product.properties["isBackOrder"] === "false") ||  product.properties["isBackOrder"] ===
                                                                            undefined ) &&
                                                                        product.warehouses?.length !== 0 &&
                                                                        tooltip(
                                                                            "inStock",
                                                                            siteMessage(
                                                                                "ProductAvailability_Available_Content_Tooltip",
                                                                            ),
                                                                            siteMessage(
                                                                                "ProductAvailability_Available_Title_Tooltip",
                                                                            ),
                                                                        )}

                                                                    {!quoteRequired &&
                                                                        product.properties["isBackOrder"] !==
                                                                            undefined &&
                                                                        product.properties["isBackOrder"] === "true" &&
                                                                        product.warehouses?.length !== 0 &&
                                                                        tooltip(
                                                                            "inStockAndBackOrder",
                                                                            siteMessage(
                                                                                "ProductAvailability_backOrder_Content_Tooltip",
                                                                            ),
                                                                            siteMessage(
                                                                                "ProductAvailability_backOrder_Title_Tooltip",
                                                                            ),
                                                                            leadTimeValue ?? "",
                                                                            hasLeadTime ?? false,
                                                                        )}
                                                                </Typography>
                                                            </GridItem>
                                                            {hasReturnableValue && (
                                                                <GridItem {...styles.attributeValueGridItem}>
                                                                    <Typography {...styles.attributeValueText}>
                                                                        {product.properties?.isCustNCNR === "" ||
                                                                        !product.properties?.isCustNCNR ||
                                                                        product.quoteRequired
                                                                            ? "--"
                                                                            : product.properties?.isCustNCNR === "true"
                                                                              ? "No"
                                                                              : "Yes"}
                                                                    </Typography>
                                                                </GridItem>
                                                            )}
                                                            {/* VERNDALE CUSTOMIZATION END - Additional attributes */}
                                                        </GridContainer>
                                                    </StyledWrapper>
                                                </StyledWrapper>
                                            );
                                        })}
                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledWrapper>
                        </GridItem>
                    </GridContainer>
                </GridItem>
                <GridItem {...styles.nextArrowGridItem}>
                    {slideDetails.slides.length > slidesToScroll && (
                        <Button
                            {...styles.nextArrowButton}
                            onClick={() => {
                                embla && embla.scrollNext();
                                emblaSecond && emblaSecond.scrollNext();
                            }}
                            disabled={!canScrollNext}
                            aria-label={translate("Next")}
                            data-test-selector="nextBtn"
                        >
                            <ButtonIcon src={ChevronRight} />
                        </Button>
                    )}
                </GridItem>
            </GridContainer>
            <span ref={afterCarousel} tabIndex={-1} />
        </>
    );
};

Carousel.defaultProps = {
    maxNumberOfColumns: 4,
};

export default connect(mapStateToProps, null)(withIsInShell(withTheme(Carousel)));
